import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@core/app/store/store";
import dayjs from "dayjs";

interface CalendarState {
  asideWidth: number;
  view: string;
  showWeekends: boolean;
  choosedDate: string;
}

const loadStateFromLocalStorage = (): Partial<CalendarState> => {
  const storedAsideWidth = localStorage.getItem("calendarAsideWidth");
  const storedView = localStorage.getItem("calendarView");
  const storedShowWeekends = localStorage.getItem("calendarShowWeekends");
  const storedChoosedDate = localStorage.getItem("calendarChoosedDate");

  let asideWidth = 0;
  let view = "month";
  let showWeekends = true;
  let choosedDate = dayjs().toISOString();

  if (storedAsideWidth) {
    try {
      asideWidth = JSON.parse(storedAsideWidth);
    } catch {
      console.error("❌ Failed to parse asideWidth from localStorage");
    }
  }

  if (storedView) {
    try {
      view = storedView;
    } catch {
      console.error("❌ Failed to parse view from localStorage");
    }
  }

  if (storedShowWeekends) {
    showWeekends = storedShowWeekends === "true";
  }

  if (storedChoosedDate) {
    choosedDate = storedChoosedDate;
  }

  return { asideWidth, view, showWeekends, choosedDate };
};

const saveShowWeekendsToLocalStorage = (value: boolean) => {
  localStorage.setItem("calendarShowWeekends", String(value));
};

const saveChoosedDateToLocalStorage = (value: string) => {
  localStorage.setItem("calendarChoosedDate", value);
};

const saveAsideWidthToLocalStorage = (value: number) => {
  localStorage.setItem("calendarAsideWidth", JSON.stringify(value));
};

const saveViewToLocalStorage = (value: string) => {
  localStorage.setItem("calendarView", value);
};

const initialState: CalendarState = {
  asideWidth: 0,
  view: "month",
  showWeekends: true,
  choosedDate: dayjs().toISOString(),
  ...loadStateFromLocalStorage(),
};

const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    setAsideWidth(state, action: PayloadAction<number>) {
      state.asideWidth = action.payload;
      saveAsideWidthToLocalStorage(state.asideWidth);
    },
    storeView(state, action: PayloadAction<string>) {
      state.view = action.payload;
      saveViewToLocalStorage(state.view);
    },
    setShowWeekends(state, action: PayloadAction<boolean>) {
      state.showWeekends = action.payload;
      saveShowWeekendsToLocalStorage(state.showWeekends);
    },
    setChoosedDate(state, action: PayloadAction<string>) {
      state.choosedDate = action.payload;
      saveChoosedDateToLocalStorage(state.choosedDate);
    },
  },
});

export const {
  setAsideWidth,
  storeView,
  setShowWeekends,
  setChoosedDate,
} = calendarSlice.actions;

export const selectCalendarAsideWidth = (state: RootState) =>
  state.calendar.asideWidth;

export const selectCalendarView = (state: RootState) =>
  state.calendar.view;

export const selectCalendarShowWeekends = (state: RootState) =>
  state.calendar.showWeekends;

export const selectCalendarChoosedDate = (state: RootState) =>
  state.calendar.choosedDate;

export default calendarSlice.reducer;
